import { useCreateGroupClassMutation } from '~/store/services/groupClass';
import GroupClassAccordion, {
  type GroupClassFormValues,
} from './GroupClassAccordion';
import {
  DateFormat,
  FAIconName,
  GroupClassIllustrations,
  useToastMessage,
} from '@equip.health/ui';
import { GROUP_CLASS_CREATE_ERROR_TEXT } from '~/lib/constants/calendar.constants';
import {
  getGroupClassRegistrationCriteriaProps,
  getRegistrationEndDate,
} from '~/lib/util/classes/groupClasses.util';
import { isEmpty } from 'lodash';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { GroupClassEvents } from '~/analytics/groupClassEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { DateTime } from 'luxon';

type AddGroupClassData = {
  handleGroupClassCreationSuccess?: ({
    id,
    isViewClass,
  }: {
    id: string;
    isViewClass: boolean;
  }) => void;
};

type AddGroupClassAccordionProps = {
  data: AddGroupClassData;
  dismissAddGroupClass: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
};

const AddGroupClassAccordion = (props: AddGroupClassAccordionProps) => {
  const { dismissAddGroupClass, isExpanded, setIsExpanded, data } = props;
  const [
    createGroupClass,
    {
      data: createGroupClassResponse,
      isLoading: isCreateGroupClassLoading,
      isSuccess: isSubmitted,
    },
  ] = useCreateGroupClassMutation();
  const { trackEvent } = useAnalytics();
  const { createToast } = useToastMessage();

  const handleSubmit = (values: GroupClassFormValues) => {
    const {
      isAllClassesRequired,
      duration,
      endDate,
      frequency,
      startDate,
      maximumAttendees,
      isRegistrationEndsAfterFirstClass,
      isPromotedInEquipApp,
    } = values;

    trackEvent(GroupClassEvents.createGroupAccordionCreateGroupButtonClicked, {
      duration,
      endDate: endDate
        ? DateTime.fromJSDate(endDate).toFormat(DateFormat.shortMonthDayYear)
        : null,
      groupTitle: values.title,
      hosts: values.hosts.map((option) => ({
        name: option.firstName + ' ' + option.lastName,
        role: option.providerType,
      })),
      promoteInEquipApp: isPromotedInEquipApp,
      recurrence: frequency,
      registrationCriteria: getGroupClassRegistrationCriteriaProps(values),
      Source: AnalyticsSourceValues.CALENDAR_GROUPS_TAB,
      startDate: DateTime.fromJSDate(startDate).toFormat(
        DateFormat.shortMonthDayYear,
      ),
      startTime: DateTime.fromJSDate(startDate).toFormat(DateFormat.time),
    });

    createGroupClass({
      ...values,
      ageRanges: isEmpty(values.ageRanges) ? null : values.ageRanges,
      attendeeRoles: isEmpty(values.attendeeRoles)
        ? null
        : values.attendeeRoles,
      diagnoses: isEmpty(values.diagnoses) ? null : values.diagnoses,
      duration,
      endDate,
      frequency,
      hosts: values.hosts.map((host) => host.externalId),
      iconName: values.iconName as GroupClassIllustrations,
      isPromoted: isPromotedInEquipApp,
      maximumAttendees,
      mustAttendAll: isAllClassesRequired,
      registrationEnds: isRegistrationEndsAfterFirstClass
        ? getRegistrationEndDate(startDate, duration)
        : null,
      startDate,
      treatmentModalities: isEmpty(values.treatmentModalities)
        ? null
        : values.treatmentModalities,
      treatmentPrograms: isEmpty(values.treatmentPrograms)
        ? null
        : values.treatmentPrograms,
    })
      .unwrap()
      .then((response) => {
        data.handleGroupClassCreationSuccess?.({
          id: response.data,
          isViewClass: false,
        });
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'create-group-toast',
          text: GROUP_CLASS_CREATE_ERROR_TEXT,
          variant: 'critical',
        });
      });
  };
  const handleViewClass = () => {
    const groupClassExternalId = createGroupClassResponse?.data;
    if (groupClassExternalId) {
      data.handleGroupClassCreationSuccess?.({
        id: groupClassExternalId,
        isViewClass: true,
      });
    }
  };
  return (
    <GroupClassAccordion
      isExpanded={isExpanded}
      isLoading={isCreateGroupClassLoading}
      isSubmitted={isSubmitted}
      mode="add"
      onCancel={dismissAddGroupClass}
      onSubmit={handleSubmit}
      onViewClass={handleViewClass}
      setIsExpanded={setIsExpanded}
    />
  );
};

export default AddGroupClassAccordion;
